import { useCurrentUser } from '@features/app-hooks'
import { formatAuthError } from '@features/auth'
import { useDerivedState } from '@st/react-util/use-derived-state'
import type { AuthError } from '@st/sdk'
import { Button, Dialog, DialogButtons, Modal, TextInput } from '@st/theme'
import { formatUSPhoneNumber } from '@st/util/input-format'
import { useState } from 'react'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { OrganizationSettingsScaffold } from './st-organization-settings-scaffold'
import type { TwoFactorState } from './st-account-settings-module'

type AccountAction = { type: 'enableTwoFactorAuth' } | { type: 'disableTwoFactorAuth' }

export function OrganizationAccountSettingsPage() {
  const user = useCurrentUser()
  const [action, setAction] = useState<AccountAction | undefined>(undefined)
  const accountSettings = useProcess('stAccountSettings')
  const settings = useSelect('stAccountSettings', (state) => state.settings)
  const twoFactorFinishing = useSelect(
    'stAccountSettings',
    (state) => state.twoFactor.status == 'finishing'
  )

  function closeTwoFactorDialog() {
    accountSettings.send({ type: 'resetTwoFactorFlow' })
    setAction(undefined)
  }

  return (
    <OrganizationSettingsScaffold title="Settings">
      <h2 className="text-xl text-gray-800">Account</h2>
      <p className="text-base text-gray-600">Logged in as {user?.email}</p>

      <div className="gap-y mt-5 flex flex-col items-start gap-8">
        <ProfileNameEditor />

        {match(user?.mfaMethod)
          .with('phone_number', () => (
            <div className="flex flex-col items-start gap-2">
              <p>Two Factor auth is enabled with phone number {user?.phoneNumber}</p>
              <Button
                variant="default"
                disabled={twoFactorFinishing}
                onClick={() => setAction({ type: 'disableTwoFactorAuth' })}
              >
                Disable Two Factor Authentication
              </Button>
            </div>
          ))
          .otherwise(() => (
            <div className="flex flex-col items-start gap-2">
              <p>Two Factor auth is disabled.</p>
              <Button
                variant="default"
                disabled={twoFactorFinishing}
                onClick={() => setAction({ type: 'enableTwoFactorAuth' })}
              >
                Enable Two Factor Authentication
              </Button>
            </div>
          ))}

        {match(settings)
          .with({ status: 'failed' }, () => (
            <div className="flex flex-col items-start gap-2" role="alert">
              <p>We could not load account settings. Please check your connection and try again.</p>
              <Button
                variant="primary"
                onClick={() => accountSettings.send({ type: 'retrySettingsLoad' })}
              >
                Retry
              </Button>
            </div>
          ))
          .with({ status: 'loaded' }, ({ emailNotificationsEnabled, saving }) => (
            <div className="flex flex-col items-start gap-1">
              <div className="flex flex-row items-center gap-2">
                <p>Email notifications enabled</p>
                <input
                  type="checkbox"
                  className={match(saving)
                    .with(true, () => '')
                    .with(false, () => 'cursor-pointer')
                    .exhaustive()}
                  checked={emailNotificationsEnabled}
                  disabled={saving}
                  onChange={() => accountSettings.send({ type: 'toggleEmailNotifications' })}
                />
              </div>

              <p className="text-base text-gray-600">
                If disabled, you will no longer receive emails from StanfordTax.
              </p>
            </div>
          ))
          .otherwise(() => null)}
      </div>

      {match(action)
        .with({ type: 'enableTwoFactorAuth' }, () => (
          <Modal isOpen={true}>
            <EnableTwoFactorAuthDialog onClose={closeTwoFactorDialog} />
          </Modal>
        ))
        .with({ type: 'disableTwoFactorAuth' }, () => (
          <Modal isOpen={true}>
            <DisableTwoFactorAuthDialog onClose={closeTwoFactorDialog} />
          </Modal>
        ))
        .otherwise(() => null)}
    </OrganizationSettingsScaffold>
  )
}

function ProfileNameEditor() {
  const user = useCurrentUser()
  const accountSettings = useProcess('stAccountSettings')
  const profileStatus = useSelect('stAccountSettings', (state) => state.profile.status)
  const [profileName, setProfileName] = useDerivedState(user?.name || '')

  function saveProfileName() {
    if (!user || !profileName.trim()) return
    accountSettings.send({ type: 'saveProfileName', name: profileName })
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <p>Full name</p>
      <div className="flex flex-row items-stretch gap-2">
        <TextInput
          className="max-w-md"
          placeholder="Enter your full name"
          autoComplete="off"
          value={profileName}
          onChange={setProfileName}
        />
        <Button
          variant="primary"
          onClick={saveProfileName}
          disabled={profileStatus == 'saving' || !profileName.trim() || profileName == user?.name}
        >
          {match(profileStatus)
            .with('saving', () => 'Saving...')
            .with('idle', () => 'Save')
            .exhaustive()}
        </Button>
      </div>
    </div>
  )
}

function EnableTwoFactorAuthDialog({ onClose }: { onClose: () => void }) {
  const accountSettings = useProcess('stAccountSettings')
  const twoFactor = useSelect('stAccountSettings', (state) => state.twoFactor)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [code, setCode] = useState('')
  const [invalidPhoneNumber, setInvalidPhoneNumber] = useState(false)

  function onSendVerificationCode() {
    if (invalidPhoneNumber) return
    accountSettings.send({ type: 'sendEnableTwoFactorCode', phoneNumber, currentPassword })
  }

  function onVerifyCode() {
    accountSettings.send({ type: 'verifyEnableTwoFactorCode', code })
  }

  return match(twoFactor)
    .with({ status: P.union('idle', 'sendingEnableCode') }, (state) => {
      const sending = state.status == 'sendingEnableCode'
      return (
        <Dialog
          title="Enable Two Factor Authentication"
          subtitle="Two factor authentication is a security feature that requires two forms of identification to access your account."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSendVerificationCode}
                disabled={
                  sending ||
                  phoneNumber.trim() == '' ||
                  currentPassword.trim() == '' ||
                  invalidPhoneNumber
                }
              >
                {match(sending)
                  .with(true, () => 'Sending code...')
                  .with(false, () => 'Send verification code')
                  .exhaustive()}
              </Button>
            </DialogButtons>
          }
        >
          <div className="flex flex-col gap-2">
            <TextInput
              className="w-full"
              placeholder="Enter your phone number ex +1 (443) 997-3253"
              value={phoneNumber}
              onChange={setPhoneNumber}
              onBlur={() => {
                const formattedNumber = formatUSPhoneNumber(phoneNumber)
                if (!formattedNumber) {
                  setInvalidPhoneNumber(true)
                  return
                }
                setInvalidPhoneNumber(false)
                setPhoneNumber(formattedNumber)
              }}
            />

            <TextInput
              className="w-full"
              type="password"
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={setCurrentPassword}
            />

            {match({ invalidPhoneNumber, error: getTwoFactorError(state) })
              .with({ invalidPhoneNumber: true }, () => (
                <p className="text-red-500">Invalid phone number</p>
              ))
              .with({ error: { type: P.string } }, ({ error }) => (
                <p className="text-red-500">{formatAuthError(error)}</p>
              ))
              .otherwise(() => null)}
          </div>
        </Dialog>
      )
    })
    .with({ status: P.union('enableCodeSent', 'verifyingEnableCode') }, (state) => {
      const verifying = state.status == 'verifyingEnableCode'
      return (
        <Dialog
          title="Enable Two Factor Authentication"
          subtitle={`We sent a verification code to your phone number ${phoneNumber}. Please enter it below.`}
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onVerifyCode} disabled={verifying || code == ''}>
                {match(verifying)
                  .with(true, () => 'Verifying phone number...')
                  .with(false, () => 'Verify phone number')
                  .exhaustive()}
              </Button>
            </DialogButtons>
          }
        >
          <div className="flex flex-col gap-2">
            <input
              type="text"
              autoFocus
              autoComplete="off"
              className="rounded-md border-0 py-1.5 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
              placeholder="Enter the 6 digit verification code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            {renderTwoFactorError(state)}
          </div>
        </Dialog>
      )
    })
    .with({ status: 'enableSucceeded' }, () => (
      <Dialog
        title="Two Factor Authentication Enabled"
        subtitle="Two factor authentication is now enabled for your account."
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      />
    ))
    .with({ status: 'finishing' }, () => null)
    .otherwise(unexpectedTwoFactorDialogState('enable'))
}

function DisableTwoFactorAuthDialog({ onClose }: { onClose: () => void }) {
  const accountSettings = useProcess('stAccountSettings')
  const twoFactor = useSelect('stAccountSettings', (state) => state.twoFactor)
  const [currentPassword, setCurrentPassword] = useState('')
  const [code, setCode] = useState('')

  function onSendDisableCode() {
    accountSettings.send({ type: 'sendDisableTwoFactorCode' })
  }

  function onDisable() {
    accountSettings.send({ type: 'disableTwoFactor', currentPassword, code })
  }

  return match(twoFactor)
    .with({ status: P.union('idle', 'sendingDisableCode') }, (state) => {
      const sending = state.status == 'sendingDisableCode'
      return (
        <Dialog
          title="Disable Two Factor Authentication"
          subtitle="Are you sure you want to disable two factor authentication? Your account will be less secure."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSendDisableCode}
                disabled={sending || currentPassword.trim() == ''}
              >
                {match(sending)
                  .with(true, () => 'Sending code...')
                  .with(false, () => 'Send verification code')
                  .exhaustive()}
              </Button>
            </DialogButtons>
          }
        >
          <div className="flex flex-col gap-2">
            <TextInput
              className="w-full"
              type="password"
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={setCurrentPassword}
            />
            {renderTwoFactorError(state)}
          </div>
        </Dialog>
      )
    })
    .with({ status: P.union('disableCodeSent', 'disabling') }, (state) => {
      const disabling = state.status == 'disabling'
      return (
        <Dialog
          title="Disable Two Factor Authentication"
          subtitle="We sent a verification code to your phone number. Please enter it below."
          buttons={
            <DialogButtons>
              <Button variant="default" onClick={onClose} disabled={disabling}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onDisable}
                disabled={disabling || code.trim() == ''}
              >
                {match(disabling)
                  .with(true, () => 'Disabling...')
                  .with(false, () => 'Disable')
                  .exhaustive()}
              </Button>
            </DialogButtons>
          }
        >
          <div className="flex flex-col gap-2">
            <input
              type="text"
              autoFocus
              autoComplete="off"
              className="rounded-md border-0 py-1.5 text-base text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
              placeholder="Enter the 6 digit verification code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            {renderTwoFactorError(state)}
          </div>
        </Dialog>
      )
    })
    .with({ status: 'disableSucceeded' }, () => (
      <Dialog
        title="Two Factor Authentication Disabled"
        subtitle="Two factor authentication is now disabled for your account."
        buttons={
          <DialogButtons>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      />
    ))
    .with({ status: 'finishing' }, () => null)
    .otherwise(unexpectedTwoFactorDialogState('disable'))
}

function getTwoFactorError(state: TwoFactorState): AuthError | undefined {
  return match(state)
    .returnType<AuthError | undefined>()
    .with({ status: P.union('idle', 'enableCodeSent', 'disableCodeSent') }, ({ error }) => error)
    .otherwise(() => undefined)
}

function renderTwoFactorError(state: TwoFactorState) {
  return match(getTwoFactorError(state))
    .with({ type: P.string }, (error) => <p className="text-red-500">{formatAuthError(error)}</p>)
    .otherwise(() => null)
}

function unexpectedTwoFactorDialogState(dialog: 'enable' | 'disable') {
  return (state: TwoFactorState): never => {
    throw new Error(`Unexpected ${state.status} state in ${dialog} two-factor dialog`)
  }
}
